import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAuthModal } from "@/context/AuthModalContext";
import { toast } from "sonner";
import { Heart, ArrowLeft } from "lucide-react";
import { ActorAvatar } from "@/components/ActorAvatar";
import { Seo } from "@/components/Seo";

const HERO = "https://images.unsplash.com/photo-1579539760267-b2e78d9d735e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function ActorDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { openAuthModal } = useAuthModal();
  const [actor, setActor] = useState(null);
  const [me, setMe] = useState(null);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setMe(data);
    } catch {
      setMe({ followed_actors: [] });
    }
  }, []);

  useEffect(() => {
    api.get(`/actors/${id}`).then((r) => setActor(r.data)).catch(() => setActor(false));
    loadMe();
  }, [id, loadMe]);

  if (actor === false) return <div className="mx-auto max-w-3xl px-6 py-20 text-center font-serif text-2xl">Actor not found.</div>;
  if (!actor || !me) return <div className="mx-auto max-w-7xl px-6 py-20 text-muted-foreground">Loading…</div>;

  const following = me.followed_actors?.includes(actor.name);
  const followNow = async () => {
    const { data } = await api.post(`/watchlist/follow-actor/${encodeURIComponent(actor.name)}`);
    toast.success(data.following ? `Following ${actor.name}` : `Unfollowed ${actor.name}`);
    await loadMe();
  };
  const doFollow = () => (user ? followNow() : openAuthModal(followNow, `Sign in to follow ${actor.name} and get alerts about their shows.`));

  return (
    <div>
      <Seo
        title={`${actor.name} | West End performer`}
        description={`See which West End shows ${actor.name} is in, and follow them for alerts when their cast changes are announced.`}
      />
      <section className="relative overflow-hidden hero-scrim grain" style={{ "--hero-img": `url(${HERO})` }}>
        <div className="relative z-10 mx-auto max-w-5xl px-4 py-14 sm:px-6">
          <Link to="/shows" className="inline-flex items-center gap-1.5 text-sm text-amber-100/70 hover:text-amber-50">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <p className="mt-4 overline-label text-[#C89B3C]">Actor</p>
          <h1 className="mt-2 flex items-center gap-4 font-serif text-4xl font-semibold text-amber-50 sm:text-5xl">
            <ActorAvatar name={actor.name} photoUrl={actor.photo_url} size={96} testId="actor-avatar" /> {actor.name}
          </h1>
          <button
            onClick={doFollow}
            data-testid="actor-follow-button"
            className={`mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold transition-colors ${
              following ? "bg-amber-50 text-black" : "border border-amber-100/40 text-amber-50 hover:bg-white/10"
            }`}
          >
            <Heart className={`h-4 w-4 ${following ? "fill-current" : ""}`} /> {following ? "Following" : "Follow"}
          </button>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <h2 className="font-serif text-2xl font-semibold">Currently in</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {(!actor.roles || actor.roles.length === 0) && <p className="text-muted-foreground">Not currently linked to any show.</p>}
          {actor.roles?.map((r, i) => (
            <Link
              key={i}
              to={`/shows/${r.show_id}`}
              data-testid={`actor-role-${i}`}
              className="rounded-xl border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <p className="font-serif text-lg font-semibold">{r.show_name}</p>
              <p className="mt-1 text-sm text-muted-foreground">as {r.role}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
